import { useRef, useState } from 'react';

import { ProofOfReserve } from '@flipster/apis/v1/support/types';
import { FormattedMessage } from '@flipster/i18n';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import { Datetime } from '@flipster/react/design-utils/datetime';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { NavigationHeightVariableKey } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { flex, grid, stack } from '@flipster/styled-system/patterns';
import { DateFormatter } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { SupportEvent } from '@flipster/utils/segment/events/support';

import { CardItem } from './card-item';
import { Navigator } from './navigator';

const Dropdown = createDropdownSelect();

interface Props {
  auditTime?: string;
  setAuditTime: (newAuditTime?: string) => void;
  isFetching: boolean;
  auditTimes?: string[];
  proofOfReserves?: ProofOfReserve[];
  verificationMechanism?: string;
  merkleRootHash?: string;
}

export const CardList = ({
  proofOfReserves,
  auditTimes,
  isFetching,
  auditTime,
  setAuditTime,
  verificationMechanism,
  merkleRootHash,
}: Props) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [listPage, setListPage] = useState<number>(0);
  const { isMobile } = useBreakpoint();

  const pagePerItem = isMobile ? 4 : 8;
  const cardItems = proofOfReserves?.slice(pagePerItem * listPage, pagePerItem * (listPage + 1)) ?? [];

  const onChangeListPage = (page: number) => {
    setListPage(page);
    if (isMobile) listRef.current?.scrollIntoView({ block: 'start', inline: 'start' });
  };

  return (
    <div
      className={grid({
        gridTemplateColumns: '1fr min-content',
        gridTemplateAreas: `
          "select ."
          "metadata arrow-right"
          "card-list card-list"
          "arrow-bottom ."
        `,
      })}
      style={{ scrollMarginTop: `var(${NavigationHeightVariableKey})` }}
      ref={listRef}
    >
      <div
        className={flex({
          gridArea: 'select',
          flexDirection: { base: 'column', tablet: 'row' },
          alignItems: { tablet: 'center' },
          gap: 16,
          mb: 24,
        })}
      >
        <span className={css({ textStyle: 'Title/S', color: 'inverse.white' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Audit_time' />
        </span>
        <Dropdown.Root
          selected={auditTime ?? auditTimes?.[0]}
          onChange={(v) => {
            setAuditTime(v);
            onChangeListPage(0);
            segment.track(SupportEvent.ProofOfReservesAuditTimeClick, { audit_time: v });
          }}
        >
          <Dropdown.TriggerChip type='square' />
          <Dropdown.Select>
            {(auditTimes ?? []).map((t) => (
              <Dropdown.Item
                key={t}
                value={t}
                label={<Datetime value={t} format={DateFormatter.ShortYearDateTimeSec} isUTC withUTCText />}
              />
            ))}
          </Dropdown.Select>
        </Dropdown.Root>
      </div>
      {verificationMechanism && merkleRootHash && (
        <div className={stack({ gridArea: 'metadata', gap: 10, mb: 24 })}>
          <div
            className={stack({
              color: 'content.default.level-5',
              direction: { base: 'column', tablet: 'row' },
              gap: { base: 0, tablet: 8 },
            })}
          >
            <span className={css({ textStyle: 'Text/L/Strong' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Verification_Mechanism' />
            </span>
            <span className={css({ textStyle: 'Text/L/Regular' })}>{verificationMechanism}</span>
          </div>
          <div
            className={stack({
              color: 'content.default.level-5',
              direction: { base: 'column', tablet: 'row' },
              gap: { base: 0, tablet: 8 },
            })}
          >
            <span className={css({ textStyle: 'Text/L/Strong', flexShrink: 0 })}>
              <FormattedMessage id='Autogen_common.r251126.s_Merkle_root_hash' />
            </span>
            <span
              className={css({
                textStyle: 'Text/L/Monospace/Regular',
                wordBreak: 'break-all',
                flexWrap: 'nowrap',
                lineClamp: { base: 'none', tablet: 1 },
              })}
            >
              {merkleRootHash}
            </span>
          </div>
        </div>
      )}
      <ul
        className={grid({
          gridArea: 'card-list',
          columns: { base: 1, tablet: 4 },
          gridTemplateRows: { tablet: 'repeat(2, 1fr)' },
          gap: { base: 12, tablet: 18 },
          tabletDown: { '& > :nth-child(n+5)': { display: 'none' } },
          mb: { base: 12, tablet: 0 },
        })}
      >
        {cardItems?.map((item) => <CardItem isLoading={isFetching} item={item} key={`proof-card-${item.currency}`} />)}
      </ul>
      <Navigator
        current={listPage}
        onChange={onChangeListPage}
        per={pagePerItem}
        total={proofOfReserves?.length || 0}
        css={{
          gridArea: { base: 'arrow-bottom', tablet: 'arrow-right' },
          alignSelf: { tablet: 'end' },
          mb: { tablet: 24 },
        }}
      />
    </div>
  );
};
